import React from 'react';

const stepColors = [
  '#4A6CF7', '#7C3AED', '#EC4899', '#F59E0B',
  '#10B981', '#3B82F6', '#8B5CF6', '#EF4444',
  '#14B8A6', '#F97316',
];

function KeyInsights({ data }) {
  const { missions, optimalOrder, orderEffects, keyTopics, keyItemsSimultaneous, keyItemsTemporal, diagToForm } = data;
  const keyMissions = missions.filter(m => m.isKey);

  return (
    <div className="key-insights">
      {/* 1. 최적 학습 순서 */}
      <div className="insight-card">
        <h3>🗺️ 최적 학습 순서</h3>
        <p>데이터 분석 결과, 아래 순서로 학습하면 단원평가에서 가장 좋은 결과를 얻을 수 있어요.</p>
        <div className="order-flow">
          {optimalOrder.map((step, idx) => {
            const isKey = keyTopics.find(kt => kt.topic === step.topic && kt.correlation > 0.45);
            return (
              <React.Fragment key={idx}>
                <span
                  className="order-step"
                  style={{
                    background: isKey
                      ? 'linear-gradient(135deg, #FF6B6B, #FF8E53)'
                      : stepColors[idx % stepColors.length],
                    fontSize: isKey ? '0.9rem' : '0.85rem',
                  }}
                >
                  {step.order}. {step.topic.length > 12 ? step.topic.slice(0, 12) + '...' : step.topic}
                  {isKey && ' 🔑'}
                </span>
                {idx < optimalOrder.length - 1 && <span className="order-arrow">→</span>}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* 2. 핵심 미션 요약 */}
      <div className="insight-card insight-highlight">
        <h3>🔑 핵심 미션 (꼭 완료하세요!)</h3>
        <ul>
          {keyMissions.map(m => (
            <li key={m.order} style={{ marginBottom: 8 }}>
              <strong>미션 {m.order}. {m.topic}</strong>
              {' '} — 단원 영향력 {(m.correlation * 100).toFixed(0)}%
              {m.bonusMultiplier > 1 && `, 점수 x${m.bonusMultiplier} 보너스`}
              {m.difficulty === 'hard' && ' ⚠️ 난이도 높음'}
            </li>
          ))}
        </ul>
      </div>

      {/* 3. Key 문항 TOP 5 */}
      <div className="insight-card insight-danger">
        <h3>⚠️ 틀리면 위험한 문항 TOP 5</h3>
        <p style={{ marginBottom: 10 }}>이 문항을 틀리면 이후 문항도 연쇄적으로 틀릴 확률이 높아요!</p>
        {keyItemsSimultaneous.slice(0, 5).map((item, idx) => (
          <div key={idx} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 12px', background: '#FFF5F5', borderRadius: 8, marginBottom: 6,
          }}>
            <span style={{
              width: 28, height: 28, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: '#FF6B6B', color: 'white', fontWeight: 700, fontSize: '0.8rem',
            }}>
              {idx + 1}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                문항 {item.itemId} ({item.topic})
              </div>
              <div style={{ fontSize: '0.75rem', color: '#718096' }}>
                {item.evalType} | 오답률 {(item.wrongRate * 100).toFixed(0)}% | 영향 {item.impactCount}개 문항
              </div>
            </div>
            <span style={{
              background: 'linear-gradient(135deg, #FF6B6B, #FF8E53)',
              color: 'white', padding: '3px 10px', borderRadius: 8,
              fontSize: '0.8rem', fontWeight: 700,
            }}>
              Key {(item.keyScore * 100).toFixed(0)}
            </span>
          </div>
        ))}
      </div>

      {/* 4. 시간 순서 Key 문항 TOP 5 */}
      {keyItemsTemporal && keyItemsTemporal.length > 0 && (
        <div className="insight-card" style={{ borderLeftColor: '#7C3AED' }}>
          <h3>⏱️ 먼저 틀리면 위험한 문항 TOP 5</h3>
          <p style={{ marginBottom: 10 }}>
            이 문항을 <strong>먼저</strong> 틀린 학생이 이후 문항도 연쇄적으로 틀렸어요.
          </p>
          {keyItemsTemporal.slice(0, 5).map((item, idx) => (
            <div key={idx} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', background: '#F3E5F5', borderRadius: 8, marginBottom: 6,
            }}>
              <span style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: '#7C3AED', color: 'white', fontWeight: 700, fontSize: '0.8rem',
              }}>
                {idx + 1}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                  문항 {item.itemId} ({item.topic})
                </div>
                <div style={{ fontSize: '0.75rem', color: '#718096' }}>
                  {item.evalType} | 후속 영향 {item.subsequentImpact}개 문항
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 5. 형성평가 순서 효과 */}
      {orderEffects && orderEffects.length > 0 && (
        <div className="insight-card">
          <h3>🔄 형성평가 순서가 중요해요!</h3>
          <p style={{ marginBottom: 10 }}>
            실제 데이터에서 형성평가를 어떤 순서로 풀었는지에 따라 단원평가 성적이 달라졌어요.
          </p>
          {orderEffects.slice(0, 5).map((oe, idx) => {
            const absDiff = Math.abs(oe.diff * 100);
            if (absDiff < 0.5) return null;

            return (
              <div key={idx} style={{
                padding: '10px 14px', background: '#F0F4FF', borderRadius: 10,
                marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    {oe.recommended}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#718096' }}>
                    이 순서가 반대보다 +{absDiff.toFixed(1)}%p 높음
                  </div>
                </div>
                <span style={{
                  background: absDiff > 2 ? '#4CAF50' : '#FFA726',
                  color: 'white', padding: '3px 8px', borderRadius: 6,
                  fontSize: '0.75rem', fontWeight: 700,
                }}>
                  +{absDiff.toFixed(1)}%p
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* 6. 진단평가 예측력 */}
      {diagToForm && diagToForm.length > 0 && (
        <div className="insight-card">
          <h3>🔍 진단평가 → 형성평가 예측</h3>
          <p style={{ marginBottom: 10 }}>
            진단평가 결과로 어떤 형성평가 토픽에서 어려워할지 미리 알 수 있어요.
          </p>
          {diagToForm.slice(0, 5).map((df, idx) => (
            <div key={idx} style={{
              padding: '8px 12px', background: '#F0F4FF', borderRadius: 8, marginBottom: 6,
            }}>
              <div style={{ fontSize: '0.85rem' }}>
                <strong>진단 문항 {df.diagItemId}</strong>
                {' → '}
                <span style={{ color: '#4A6CF7' }}>{df.formTopic}</span>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#718096' }}>
                맞힌 학생 {(df.correctAccuracy * 100).toFixed(0)}% vs 틀린 학생 {(df.wrongAccuracy * 100).toFixed(0)}%
                (차이 {(df.gap * 100).toFixed(1)}%p)
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 학습 가이드 요약 */}
      <div className="insight-card" style={{ borderLeftColor: '#4CAF50', background: '#E8F5E9' }}>
        <h3>📚 학습 가이드 요약</h3>
        <ol style={{ paddingLeft: 18, lineHeight: 2 }}>
          <li><strong>순서대로 학습하세요</strong> — 위의 최적 순서를 따르면 효과적이에요.</li>
          <li><strong>🔑 핵심 미션은 반드시 완료</strong> — 단원 이해의 핵심이에요.</li>
          <li><strong>⚠️ KEY 문항은 꼭 이해하고 넘어가세요</strong> — 틀리면 이후가 어려워져요.</li>
          <li><strong>어렵더라도 포기하지 마세요</strong> — 하위 학생도 연습하면 올라갈 수 있어요!</li>
          <li><strong>도장 모으기!</strong> — 미션을 하나씩 완료하며 점수를 모아보세요.</li>
        </ol>
      </div>
    </div>
  );
}

export default KeyInsights;
